import React, { useEffect, useState, useContext } from 'react';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import { startOfMinute } from 'date-fns';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { isNull, isString, isUndefined } from 'underscore';
import { NewTextField, SelectField, ToggleField } from '../Common/Fields';
import { updated_timeToDate, validTimeChange } from '../Common/timeUtils';
import { VehicleImageRow } from '../Common/VehicleImageRow';
import { table } from '../Jobs/Common/jobStyles';
import { paddings } from '../Common/styles/spacing';
import vehicleApi from '@/apis/vehicleApi';
import { ViewRoutesContext } from '@/components/UserManagement/ViewsRoutes';
import { format, parseISO } from 'date-fns';

const useStyles = makeStyles(() => ({
  ...paddings.all,
  rowHeight: {
    minHeight: 65,
  },
  tr: table.tr,
  heading: {
    ...table.fixedHeader,
    padding: 0,
  },
  formLabel: {
    '& .MuiFormLabel-root': {
      color: '#00000061',
    },
  },
}));

function Detail({
  vehicle: immutableVehicle,
  onChange,
  header,
  disabled,
  types = [],
  loadClasses: lClasses = [],
}) {
  const [vehicle, setVehicle] = useState(immutableVehicle);
  const [edited, setEdited] = useState(false);
  const [loadClasses, setLoadClasses] = useState(lClasses);
  const { dimensions } = useSelector((state) => state.core);
  const classes = useStyles();
  const endAddresses = useContext(ViewRoutesContext);

  const { vehicletype_id, load_classes = [], active = true } = vehicle;

  const getEndAddress = () => {
    const location = vehicle?.end_location?.Location;
    const address = location?.address;
    if (address) {
      const { street, house_number, postal_code, city, country } = address;
      return `${street ?? ''} ${house_number ?? ''}, ${postal_code ?? ''} ${city ?? ''
        }${country ? `, ${country}` : ''}`.trim();
    }
    const coordinates = location?.coordinates;
    if (coordinates) {
      const { latitude, longitude } = coordinates;
      return `Coordinates: ${latitude ?? ''}, ${longitude ?? ''}`;
    }
    return 'Location not available';
  };
  useEffect(() => {
    setDefaults(immutableVehicle);
    // TODO: fix this hook
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immutableVehicle]);

  useEffect(() => {
    if (isUndefined(loadClasses || isNull(loadClasses))) {
      vehicleApi
        .getLoadClass()
        .then(({ data: { objects } }) => setLoadClasses(objects))
        .catch((err) => console.log(err));
    }
    return () => vehicleApi.abortFetch('unmounting');
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setDefaults(data) {
    const defaults = getVehicleDefaults(data);
    const capacities = getDefaultCapacities(defaults);
    const newVehicle = {
      ...vehicle,
      ...defaults,
      ...capacities,
    };
    setVehicle(newVehicle);
    setEdited(false);
  }

  function getDefaultCapacities({
    capacity_1,
    capacity_2,
    capacity_3,
    vehicletype,
  }) {
    return [
      { key: 'capacity_1', val: capacity_1 },
      { key: 'capacity_2', val: capacity_2 },
      { key: 'capacity_3', val: capacity_3 },
    ].reduce((acc, val) => {
      if (!val.val) {
        val.val = vehicletype ? vehicletype['default_' + val.key] : undefined;
      }
      const shaped = { [val.key]: val.val };
      return { ...acc, ...shaped };
    }, {});
  }

  function getVehicleDefaults(v) {
    const missing = {};
    return {
      ...v,
      ...missing,
    };
  }

  function handleChange(e) {
    const {
      target: { value, name },
    } = e;
    let val = value;

    if (name === 'zip_codes') {
      val = value.length ? value.split(',') : null;
    }

    if (val === 'null') val = null;
    const shouldBeNumber =
      name === 'capacity_1' ||
      name === 'capacity_2' ||
      name === 'capacity_3' ||
      name === 'variable_cost_eur_per_km' ||
      name === 'traveltime_factor' ||
      name === 'max_working_time_min' ||
      name === 'length_cm' ||
      name === 'width_cm' ||
      name === 'height_cm' ||
      name === 'max_allowed_distance_km' ||
      name === 'max_load_height_cm' ||
      name === 'fixed_cost_eur';
    if (shouldBeNumber && (val || val === 0)) {
      if (typeof val === 'string') val = val.replace(',', '.');
    }
    const newVehicle = { ...vehicle, [name]: val };
    setVehicle(newVehicle);
    setEdited(true);
  }

  function save() {
    const keys = Object.keys(vehicle);
    keys.map((key) => {
      if (isString(vehicle[key]) && !vehicle[key].length) vehicle[key] = null;
    });
    if (!vehicle.name) {
      return toastr.error(immutableVehicle.name, 'Name is required');
    }
    onChange(vehicle).then((data) => {
      setEdited(false);
      setVehicle(data);
    });
  }

  function handleVehicleTypeSelect({ target: { value } }) {
    const newVehicle = {
      ...vehicle,
      vehicletype_id: value,
    };
    setVehicle({
      ...newVehicle,
      vehicletype_id: !value ? null : value,
    });
    setEdited(true);
  }

  function handleVehicleClassSelect({ target: { value } }) {
    const newVehicle = {
      ...vehicle,
      vehicle_class: value,
    };
    setVehicle(newVehicle);
    setEdited(true);
  }

  const formatTime = (isoString) => {
    const date = parseISO(isoString);
    const formattedTime = format(date, 'HH:mm');
    return formattedTime;
  };

  function selectLoadClass({ target: { value } }) {
    const newVehicle = {
      ...vehicle,
      load_classes: value,
    };
    setVehicle(newVehicle);
    setEdited(true);
  }

  function setTime(path, newValue) {
    const d = newValue?.toDate ? newValue.toDate() : newValue;
    const hhmm = newValue ? format(startOfMinute(d), 'HH:mm') : null;

    setVehicle((prev) => {
      if (path.startsWith('operation_start_window.')) {
        const key = path.split('.')[1];
        return {
          ...prev,
          operation_start_window: { ...prev.operation_start_window, [key]: hhmm },
        };
      }
      return { ...prev, [path]: hhmm };
    });
    setEdited(true);
  }

  function handleTimeChange(name, date) {
    const newVehicle = {
      ...vehicle,
      [name]: date,
    };
    if (!date) {
      setVehicle(newVehicle);
      return setEdited(true);
    }

    if (!date.isValid()) return;

    if (validTimeChange(newVehicle, name, date)) {
      setVehicle({
        ...vehicle,
        [name]: formatTime(startOfMinute(new Date(date))),
      });
      return setEdited(true);
    }
    setVehicle(newVehicle);
    setEdited(false);
  }

  const mergedDimensions =
    dimensions && dimensions.length
      ? dimensions.map((dim, i) => {
        return {
          ...dim,
          value: vehicle.capacities[i],
        };
      })
      : [];
  const loadClassValue = loadClasses?.some((lc) => lc.name === vehicle.load_class_name)
    ? vehicle.load_class_name
    : '';
  return (
    <Grid container justifyContent={'flex-start'}>
      {header && (
        <Grid item xs={12} className={classes.heading}>
          <Grid
            container
            justifyContent={'space-between'}
            alignItems={'center'}
            className={classes.tr}
          >
            <Grid item xs={12}>
              <Grid container justifyContent={'center'} alignItems={'center'}>
                <Typography color={'primary'}>{vehicle.name}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        {!!types.length && (
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.type')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <SelectField
                onSelect={handleVehicleTypeSelect}
                style={{ marginTop: 0 }}
                disabled={disabled}
                value={vehicletype_id?.toString() || 0}
                name={'vehicletype_id'}
                inputProps={{ 'data-testid': 'vehicle-type' }}
                items={[
                  <MenuItem key={0} value={0}>
                    -
                  </MenuItem>,
                  ...types.map((type) => (
                    <MenuItem value={type.id} key={type.id}>
                      {type.vehicletypename}
                    </MenuItem>
                  )),
                ]}
              />
            </Grid>
          </Grid>
        )}
        <Grid
          container
          justifyContent={'center'}
          alignItems={'center'}
          className={classes.rowHeight}
        >
          <Grid item xs={4}>
            <Typography variant={'subtitle1'}>
              {i18next.t('common:vehicleEditor.class')}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <div className={classes.pl_1}>
              <VehicleImageRow
                onChange={handleVehicleClassSelect}
                disabled={disabled}
                value={vehicle.vehicle_routing_type}
                data-testid="vehicle-class"
              />
            </div>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent={'center'}
          alignItems={'center'}
          className={classes.rowHeight}
        >
          <Grid item xs={6} container alignItems={'center'}>
            <Grid item xs={8}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.liftgate')}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <ToggleField
                checked={vehicle.has_liftgate}
                data-testid="vehicle-has-liftgate"
                variant={'primary'}
                value={vehicle.has_liftgate}
                onToggle={({ target: { checked } }) =>
                  handleChange({
                    target: { name: 'has_liftgate', value: checked },
                  })
                }
                name={'has_liftgate'}
                disabled={disabled}
              />
            </Grid>
          </Grid>
          <Grid item xs={6} container alignItems={'center'}>
            <Grid item xs={8}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.trailer')}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <ToggleField
                checked={vehicle.has_trailer}
                data-testid="vehicle-has_trailer"
                variant={'primary'}
                value={vehicle.has_trailer}
                onToggle={({ target: { checked } }) =>
                  handleChange({ target: { name: 'has_trailer', value: checked },
                  })
                }
                name="has_trailer"
                disabled={disabled}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent={'center'}
          alignItems={'center'}
          className={classes.rowHeight}
        >
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.name')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.name || ''}
                isRequired={true}
                inputProps={{ 'data-testid': 'vehicle-name' }}
                disabled={disabled}
                name={'name'}
                margin={'none'}
                type={'text'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.active')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <ToggleField
                checked={active}
                data-testid="vehicle-active"
                variant={'primary'}
                value={active}
                onToggle={({ target: { checked } }) =>
                  handleChange({ target: { name: 'active', value: checked } })
                }
                name={'active'}
                disabled={disabled}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.lengthInCm')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.length_cm || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-length' }}
                label={
                  !isNaN(vehicle.length_cm)
                    ? i18next.t('common:vehicleEditor.lengthInCm')
                    : ''
                }
                name={'length_cm'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.widthInCm')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.width_cm || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-width' }}
                label={
                  !isNaN(vehicle.width_cm)
                    ? i18next.t('common:vehicleEditor.widthInCm')
                    : ''
                }
                name={'width_cm'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.heightInCm')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.height_cm || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-height' }}
                label={
                  !isNaN(vehicle.height_cm)
                    ? i18next.t('common:vehicleEditor.heightInCm')
                    : ''
                }
                name={'height_cm'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.roadWeightlimit')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.road_weight_limit_kg || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-max-allowed-weight' }}
                label={
                  !isNaN(vehicle.road_weight_limit_kg)
                    ? i18next.t('common:vehicleEditor.roadWeightlimit')
                    : ''
                }
                name={'road_weight_limit_kg'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.maxAdrPoints')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.max_adr_points || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-max-adr-points' }}
                label={
                  !isNaN(vehicle.max_adr_points)
                    ? i18next.t('common:vehicleEditor.maxAdrPoints')
                    : ''
                }
                name={'max_adr_points'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.maxAllowedKm')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.max_allowed_distance_km || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-max-allowed-km' }}
                label={
                  !isNaN(vehicle.max_allowed_distance_km)
                    ? i18next.t('common:vehicleEditor.maxAllowedKm')
                    : ''
                }
                name={'max_allowed_distance_km'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.costPerDay')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.fixed_cost_eur || ''}
                disabled={disabled}
                inputProps={{ 'data-testid': 'vehicle-daily-rate' }}
                label={
                  !isNaN(vehicle.fixed_cost_eur)
                    ? i18next.t('common:vehicleEditor.costIn')
                    : ''
                }
                name={'fixed_cost_eur'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.costPerKm')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.variable_cost_eur_per_km || ''}
                data-testid="vehicle-max-load-height"
                disabled={disabled}
                label={
                  !isNaN(vehicle.variable_cost_eur_per_km)
                    ? i18next.t('common:vehicleEditor.costPerKmShort')
                    : ''
                }
                name="max_load_height_cm"
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.maxLoadHeight')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.max_load_height_cm || ''}
                inputProps={{ 'data-testid': 'vehicle-km-rate' }}
                disabled={disabled}
                label={
                  !isNaN(vehicle.max_load_height_cm)
                    ? i18next.t('common:vehicleEditor.maxLoadHeightShort')
                    : ''
                }
                name={'max_load_height'}
                margin={'none'}
                type={'number'}
              />
            </Grid>
          </Grid>
          {mergedDimensions.map((dimension, i) => (
            <Grid
              container
              justifyContent={'flex-start'}
              alignItems={'center'}
              className={classes.rowHeight}
              key={i}
            >
              <Grid item xs={4}>
                <Typography variant={'subtitle1'}>
                  {'Max. ' +
                    dimension.dimension +
                    ' ' +
                    (dimension.unit ? '(' + dimension.unit + ')' : '')}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <NewTextField
                  onChange={handleChange}
                  value={dimension.value || ''}
                  inputProps={{ 'data-testid': 'vehicle-capacity-' + i }}
                  label={
                    dimension.unit || i18next.t('common:vehicleEditor.piece')
                  }
                  name={'capacity_' + (i + 1)}
                  fullWidth
                  margin={'none'}
                  disabled={disabled}
                  type={'number'}
                />
              </Grid>
            </Grid>
          ))}
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.operation_start_window_start')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TimePicker
                textField={(props) => (
                  <TextField
                    {...props}
                    fullWidth
                    margin="none"
                    id="operation_start_window_start"
                    name="operation_start_window_start"
                    data-testid="vehicle-operation_start_window_start"
                  />
                )}
                disabled={disabled}
                label={i18next.t('common:vehicleEditor.timePicker')}
                value={updated_timeToDate(
                  vehicle?.operation_start_window?.start
                )}
                onChange={(date) =>
                  setTime('operation_start_window.start', date)
                }
                className={classes.formLabel}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.operation_start_window_end')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TimePicker
                textField={(props) => (
                  <TextField
                    {...props}
                    margin="none"
                    id="operation_start_window_end"
                    fullWidth
                    name="operation_start_window_end"
                    data-testid="vehicle-operation_start_window_end"
                    label={i18next.t('common:vehicleEditor.timePicker')}
                  />
                )}
                disabled={disabled}
                value={updated_timeToDate(vehicle?.operation_start_window?.end)}
                onChange={(date) => setTime('operation_start_window.end', date)}
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.max_tour_duration')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <NewTextField
                onChange={handleChange}
                value={vehicle.max_working_time_min || ''}
                fullWidth
                inputProps={{ 'data-testid': 'vehicle-max-tour-duration' }}
                label={i18next.t('common:vehicleEditor.max_tour_duration')}
                name={'max_working_time_min'}
                margin={'none'}
                disabled={disabled}
                type={'number'}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.operation_end')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <TimePicker
                textField={(props) => (
                  <TextField
                    {...props}
                    margin="none"
                    fullWidth
                    id="operation_end"
                    name="operation_end"
                    data-testid="vehicle-return-time-to"
                    label={i18next.t('common:settings.timePicker')}
                  />
                )}
                disabled={disabled}
                value={updated_timeToDate(vehicle.operation_end)}
                onChange={(date) => handleTimeChange('operation_end', date)}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.roundtrip')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Select
                value={isNull(vehicle.round_trip) ? 'null' : vehicle.round_trip}
                name={'round_trip'}
                fullWidth
                data-testid="vehicle-roundtrip"
                disabled={disabled}
                onChange={(e) => handleChange(e)}
              >
                <MenuItem data-testid="roundtrip-none" value={'null'}>
                  {i18next.t('common:vehicleEditor.roundtripStandard')}
                </MenuItem>
                <MenuItem data-testid="roundtrip-on" value={true}>
                  {i18next.t('common:vehicleEditor.roundtripOn')}
                </MenuItem>
                <MenuItem data-testid="roundtrip-off" value={false}>
                  {i18next.t('common:vehicleEditor.roundtripOff')}
                </MenuItem>
              </Select>
            </Grid>
          </Grid>
          {getEndAddress() && (
            <Grid
              container
              justifyContent={'flex-start'}
              alignItems={'center'}
              className={classes.rowHeight}
            >
              <Grid item xs={4}>
                <Typography variant={'subtitle1'}>
                  {i18next.t('common:vehicleEditor.end-address')}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <NewTextField
                  onChange={handleChange}
                  value={getEndAddress()}
                  data-testid="end-address"
                  disabled={true}
                  name="end-address"
                  margin="none"
                  type="text"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
          <Grid
            container
            justifyContent={'center'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Grid item xs={4}>
              <Typography variant={'subtitle1'}>
                {i18next.t('common:vehicleEditor.loadClass')}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Select
                value={loadClassValue}
                name="load_class_name"
                fullWidth
                disabled={disabled || !loadClasses?.length}
                onChange={(e) =>
                  setVehicle((prev) => ({
                    ...prev,
                    load_class_name: e.target.value,
                  }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadClasses?.map((lc) => (
                  <MenuItem key={lc.name} value={lc.name}>
                    {lc.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Grid>
        {header && (
          <Grid
            container
            justifyContent={'flex-start'}
            alignItems={'center'}
            className={classes.rowHeight}
          >
            <Button
              color={'primary'}
              variant={'contained'}
              data-testid="save-vehicle-edit"
              disabled={!edited}
              onClick={save}
            >
              {i18next.t('common:buttons.save')}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}

Detail.propTypes = {
  vehicle: PropTypes.object.isRequired,
  loadClasses: PropTypes.array,
  header: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  types: PropTypes.array,
};

export default Detail;
